import React, { useState, useEffect } from 'react';
import Pagination from "react-js-pagination";
import {
    Box, HStack, Text, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    useDisclosure, Spinner, Input, Flex, Button, Icon, Tooltip,
    ModalFooter, Tag, VStack, Badge, ModalBody, ModalCloseButton,
    ModalHeader, ModalContent, ModalOverlay, Modal, Center, useToast
} from '@chakra-ui/react';
import moment from 'moment';
import { ExternalLinkIcon, RepeatIcon, TimeIcon, WarningIcon } from '@chakra-ui/icons';
import 'moment-timezone';
import Select from "react-select";
import { MdOutlineFilterAlt, MdOutlineFilterAltOff } from 'react-icons/md';
import { FaMobileAlt } from 'react-icons/fa';
import { LuBell, LuInfo, LuMail, LuMessageCircle, LuMessageSquare, LuPhone } from 'react-icons/lu';
import { FiCheckCircle, FiXCircle } from "react-icons/fi";
import { Menu, MenuButton, MenuList, MenuItem } from "@chakra-ui/react";
import { ChevronDownIcon, CheckIcon } from "@chakra-ui/icons";
import Prism from 'prismjs';
import "prismjs/components/prism-json";
import "prismjs/themes/prism-tomorrow.css";
import { getUserTimeZone } from '../../../../utils/DefaultFunction';
import API from '../../../../helpers/axiosInstance';
import EmailPreviewModal from '../../../TemplateManager/CreateTemplate/EmailPreviewModal';
import { decodeHtmlEntities } from '../../../TemplateManager/CreateTemplate/EmailDesigner/htmlExport';

/**
 * Props from parent:
 * - list: array of message log items
 * - totalItems: total count for pagination
 * - loading: boolean
 * - activePage: current page number
 * - onPageChange: (page) => void
 * - onFiltersChange: ({ channel, status, startDate, endDate, search }) => void
 * - onRefresh: () => void
 * - channelOptions: [{ label, value }]
 * - statusOptions: [{ label, value }]
 * - statusList: raw status objects for history lookup
 * - isDownloading: boolean
 * - onDownload: () => void
 * - autoRefreshInterval: number (ms)
 * - onAutoRefreshChange: (ms) => void
 */
export default function CSVCampaignReportWithTable({
    list = [],
    totalItems = 0,
    loading = false,
    activePage = 1,
    onPageChange,
    onFiltersChange,
    onRefresh,
    channelOptions = [],
    statusOptions = [],
    statusList = [],
    isDownloading = false,
    onDownload,
    autoRefreshInterval = 0,
    onAutoRefreshChange,
}) {
    const { isOpen: isPayloadOpen, onOpen: onPayloadOpen, onClose: onPayloadClose } = useDisclosure();
    const { isOpen: isErrorLogOpen, onOpen: onErrorLogOpen, onClose: onErrorLogClose } = useDisclosure();

    // Filter UI states (temporary, until Apply is clicked)
    const [showFilters, setShowFilters] = useState(false);
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchValue, setSearchValue] = useState('');

    // Applied filter states (sent to parent)
    const [appliedChannel, setAppliedChannel] = useState(null);
    const [appliedStatus, setAppliedStatus] = useState(null);
    const [appliedStartDate, setAppliedStartDate] = useState('');
    const [appliedEndDate, setAppliedEndDate] = useState('');
    const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

    const [isOpen, setIsOpen] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [selectedPayload, setSelectedPayload] = useState(null);
    const [selectedErrorLog, setSelectedErrorLog] = useState(null);
    const [htmlPreview, setHtmlPreview] = useState(null);
    const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
    const toast = useToast();

    const getApiHeaders = () => ({
        'x-api-key': localStorage.getItem('api-key'),
        'x-access-token': localStorage.getItem('customer-portal'),
    });

    /**
     * Fallback preview for a recorded body that cannot render.
     *
     * The log pipeline masks every digit in `message` to `****`, which destroys the HTML
     * beyond repair — `<h1>` becomes `<h****>`, `padding:16px` becomes `padding:****px`,
     * `border="0"` becomes `border="****"`, and every image URL containing a digit 404s.
     * `message_template_id` survives unmasked, so the source template can be fetched and
     * rendered to show what the email actually looks like.
     *
     * This is explicitly NOT what the recipient received — variables are unsubstituted and
     * a later template edit would show through — hence the note carried into the modal.
     */
    const loadTemplatePreview = async (templateId) => {
        if (!templateId) return;
        setTemplatePreviewLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/template-message/get-by-id?id=${templateId}`;
            const res = await API.get(url, { headers: getApiHeaders() });
            const tpl = res?.data?.data || res?.data || {};
            let combine = {};
            try {
                combine = tpl.message_combine ? JSON.parse(tpl.message_combine) : {};
            } catch (e) {
                console.error('Failed to parse message_combine for template', templateId, e);
            }
            const html = decodeHtmlEntities(combine.emailHtml || combine.html_code || tpl.html_code || '');
            if (!html.trim()) {
                toast({ title: 'No email HTML stored on this template', status: 'warning', duration: 4000, isClosable: true });
                return;
            }
            setHtmlPreview((prev) => ({ ...prev, body: html, fromTemplate: true }));
        } catch (err) {
            toast({
                title: 'Could not load the template',
                description: err?.response?.data?.message || err?.message || 'Request failed',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setTemplatePreviewLoading(false);
        }
    };

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
        }, 1000); // 1 second debounce
        return () => clearTimeout(timer);
    }, [searchValue]);

    // Notify parent when applied filters or debounced search change
    useEffect(() => {
        onFiltersChange?.({
            channel: appliedChannel?.value || null,
            status: appliedStatus?.value || null,
            startDate: appliedStartDate ? moment(appliedStartDate).format("YYYY-MM-DD") : '',
            endDate: appliedEndDate ? moment(appliedEndDate).format("YYYY-MM-DD") : '',
            search: debouncedSearchValue.trim(),
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedChannel, appliedStatus, appliedStartDate, appliedEndDate, debouncedSearchValue]);

    const applyFilters = () => {
        setAppliedChannel(selectedChannel);
        setAppliedStatus(selectedStatus);
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
        setShowFilters(false);
        onPageChange?.(1);
    };

    const clearFilters = () => {
        setSelectedChannel(null);
        setSelectedStatus(null);
        setStartDate('');
        setEndDate('');
        setAppliedChannel(null);
        setAppliedStatus(null);
        setAppliedStartDate('');
        setAppliedEndDate('');
        setSearchValue('');
        onPageChange?.(1);
    };

    // ─── Channel config ────────────────────────────────────────────────────────
    const Channels = [
        { name: "Whatsapp", icon: LuMessageCircle, color: "#00a63e", id: 2 },
        { name: "SMS", icon: LuMessageSquare, color: "#155dfc", id: 1 },
        { name: "E-Mail", icon: LuMail, color: "#9810fa", id: 3 },
        { name: "Push Notification", icon: LuBell, color: "#e60076", id: 6 },
        { name: "In-App Notification", icon: LuBell, color: "#4f39f6", id: 5 },
        { name: "Voice (IVR)", icon: LuPhone, color: "#f54a00", id: 4 },
    ];

    const getChannelInfo = (channelIdOrName) =>
        Channels.find((c) => c.id === channelIdOrName || c.name === channelIdOrName) ||
        { name: "System", icon: FaMobileAlt, color: "gray" };

    const getStatusDetails = (statusId) =>
        statusList.find((s) => s.status_id === statusId || s.id === statusId);

    // status_id 110 isn't in the /status/list response, so getStatusDetails finds
    // nothing for it — the journey falls back to this instead of a bare "Status 110"
    // with a failure (✗) icon, since a missing msg_status reads as not-success.
    const KNOWN_STATUS_FALLBACKS = { 110: { name: 'Sent to device', msg_status: '1' } };

    const getStatusColor = (statusName) => {
        const name = statusName?.toLowerCase() || "";
        if (name.includes("read") || name.includes("delivered")) return "green";
        if (name.includes("sent")) return "blue";
        if (name.includes("fail") || name.includes("undeliverable")) return "red";
        if (name.includes("accept")) return "green";
        return "gray";
    };

    const getIntervalLabel = (interval) => {
        switch (interval) {
            case 30000: return "30s";
            case 60000: return "1m";
            case 300000: return "5m";
            default: return "OFF";
        }
    };

    const getTypeId = (typeId) => {
        switch (typeId) {
            case 1: return "Authentication";
            case 2: return "Transactional";
            case 3: return "Marketing";
            case 4: return "Informational";
            default: return "Unknown";
        }
    };

    const regexCheckHtml = /<[^>]*>/;

    const openModal = (msg) => { setSelectedMessage(msg); setIsOpen(true); };
    const closeModal = () => { setIsOpen(false); setSelectedMessage(null); };

    const handleViewPayload = (payload) => {
        const parsed = JSON.parse(JSON.stringify(payload, (key, value) => {
            if (typeof value === "string" && value.startsWith("{") && value.endsWith("}")) {
                try { return JSON.parse(value); } catch { return value; }
            }
            return value;
        }));
        setSelectedPayload(JSON.stringify(parsed, null, 2));
        onPayloadOpen();
        setTimeout(() => Prism.highlightAll(), 0);
    };

    // ─── Toolbar JSX ───────────────────────────────────────────────────────────
    const toolbarJSX = (disabled) => (
        <HStack justifyContent="space-between" alignItems="center" mb={4}>
            <Text fontSize="xl" color="grey.400" fontWeight="medium">Delivery Logs</Text>
            <HStack spacing={4}>
                <Box minW="300px">
                    <Input
                        placeholder="Search by Message ID, recipient"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        size="sm"
                    />
                </Box>

                <Button size="sm" bg="var(--brand-primary)" isLoading={isDownloading} color="white"
                    onClick={onDownload} isDisabled={disabled}>
                    Export
                </Button>

                <HStack spacing={0} borderWidth="1px" borderRadius="md" overflow="hidden">
                    <Button size="sm" bg="var(--brand-primary)" borderRightRadius={0} color="white"
                        leftIcon={<RepeatIcon boxSize={3} />} onClick={onRefresh} isDisabled={disabled}>
                        Refresh
                    </Button>
                    <Menu>
                        <MenuButton as={Button} size="sm" variant="solid" bg="var(--brand-primary)"
                            borderLeftRadius={0} px={2} color="white"
                            rightIcon={<ChevronDownIcon boxSize={3} />} isDisabled={disabled}>
                            {autoRefreshInterval !== 0 && (
                                <Text ml={2} fontSize="xs" opacity={0.8}>{getIntervalLabel(autoRefreshInterval)}</Text>
                            )}
                        </MenuButton>
                        <MenuList minW="120px">
                            {[0, 30000, 60000, 300000].map((val) => (
                                <MenuItem key={val} onClick={() => onAutoRefreshChange?.(val)}>
                                    {val === 0 ? "OFF" : `Every ${getIntervalLabel(val)}`}
                                    {autoRefreshInterval === val && <Icon as={CheckIcon} ml="auto" />}
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Menu>
                </HStack>

                <Button onClick={() => setShowFilters(!showFilters)} colorScheme="blue"
                    variant={showFilters ? "solid" : "outline"} size="sm"
                    className="updated-chakra-button" isDisabled={disabled}>
                    {showFilters ? <MdOutlineFilterAltOff /> : <MdOutlineFilterAlt />}
                </Button>
            </HStack>
        </HStack>
    );

    // ─── Loading state ─────────────────────────────────────────────────────────
    if (loading) {
        return (
            <>
                {toolbarJSX(true)}
                <Box overflowX="auto" border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
                    <Table>
                        <Thead>
                            <Tr>
                                <Th w="200px">Message ID</Th>
                                <Th w="160px">Recipient</Th>
                                <Th w="400px">Message</Th>
                                <Th w="180px">Channel</Th>
                                <Th w="180px">Date & Time</Th>
                                <Th w="190px">Status</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            <Tr>
                                <Td colSpan={6} textAlign="center" py={8}>
                                    <Center h="400px">
                                        <VStack spacing={3}>
                                            <Spinner size="xl" color="blue.500" thickness="4px" />
                                            <Text fontSize="sm" color="gray.600" fontWeight="medium">Loading messages...</Text>
                                        </VStack>
                                    </Center>
                                </Td>
                            </Tr>
                        </Tbody>
                    </Table>
                </Box>
            </>
        );
    }

    // ─── Main render ───────────────────────────────────────────────────────────
    return (
        <>
            {toolbarJSX(false)}

            {/* Filter panel */}
            {showFilters && (
                <Box className="box-style-card" mb={4}>
                    <HStack spacing={4} mb={4} wrap="wrap">
                        {/* <Box minW="200px">
                            <Text fontSize="sm" mb={2} color="gray.600">Start Date:</Text>
                            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        </Box>
                        <Box minW="200px">
                            <Text fontSize="sm" mb={2} color="gray.600">End Date:</Text>
                            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                        </Box> */}
                        <Box minW="200px">
                            <Text fontSize="sm" mb={2} color="gray.600">Channel:</Text>
                            <Select options={channelOptions} placeholder="All Channels"
                                value={selectedChannel} onChange={setSelectedChannel} isClearable />
                        </Box>
                        <Box minW="200px">
                            <Text fontSize="sm" mb={2} color="gray.600">Status:</Text>
                            <Select options={statusOptions} placeholder="All Status"
                                value={selectedStatus} onChange={setSelectedStatus} isClearable />
                        </Box>
                    </HStack>
                    <HStack spacing={3} justifyContent="flex-end">
                        <Button onClick={clearFilters} variant="outline" colorScheme="gray" size="sm">Clear Filters</Button>
                        <Button onClick={applyFilters} colorScheme="blue" size="sm" className="updated-chakra-button">Apply Filters</Button>
                    </HStack>
                </Box>
            )}

            {/* Active filters display */}
            {(appliedChannel || appliedStatus || appliedStartDate || appliedEndDate || debouncedSearchValue) && (
                <Box className="box-style-card" mb={4}>
                    <Text fontSize="sm" fontWeight="semibold" color="blue.800" mb={2}>Active Filters:</Text>
                    <HStack spacing={2} wrap="wrap">
                        {appliedChannel && <Box bg="blue.100" px={2} py={1} borderRadius="md" fontSize="xs">Channel: {appliedChannel.label}</Box>}
                        {appliedStatus && <Box bg="blue.100" px={2} py={1} borderRadius="md" fontSize="xs">Status: {appliedStatus.label}</Box>}
                        {appliedStartDate && <Box bg="blue.100" px={2} py={1} borderRadius="md" fontSize="xs">From: {moment(appliedStartDate).format('DD/MM/YYYY')}</Box>}
                        {appliedEndDate && <Box bg="blue.100" px={2} py={1} borderRadius="md" fontSize="xs">To: {moment(appliedEndDate).format('DD/MM/YYYY')}</Box>}
                        {debouncedSearchValue && <Box bg="blue.100" px={2} py={1} borderRadius="md" fontSize="xs">Search: "{debouncedSearchValue}"</Box>}
                    </HStack>
                </Box>
            )}

            {/* Table */}
            <Box overflowX="auto" border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
                <Table>
                    <Thead>
                        <Tr>
                            <Th w="100px">Message ID</Th>
                            <Th w="160px">Recipient</Th>
                            <Th w="400px">Message</Th>
                            <Th w="180px">Channel</Th>
                            <Th w="180px">Date & Time</Th>
                            <Th w="190px">Status</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {list.map((item) => (
                            <Tr key={item._id}>
                                <Td whiteSpace="nowrap" color="var(--brand-primary)" cursor="pointer"
                                    onClick={() => openModal(item)} _hover={{ textDecoration: "underline" }}>
                                    <HStack spacing={1}>
                                        <Text>{item._id.slice(0, 8)}...</Text>
                                        <ExternalLinkIcon />
                                    </HStack>
                                </Td>

                                <Td whiteSpace="nowrap">{item.to || "—"}</Td>

                                <Td maxW="400px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis"
                                    title={regexCheckHtml.test(item.message) ? null : item.message}>
                                    {regexCheckHtml.test(item.message) ? (
                                        // Same reasoning as the journey modal below: this backend
                                        // masks every digit to ****, so rendering the stored body
                                        // inline (or in an iframe) shows broken markup. Route to
                                        // EmailPreviewModal, which offers the source template instead.
                                        <Text
                                            color="var(--brand-primary)"
                                            cursor="pointer"
                                            isTruncated
                                            textDecoration="underline"
                                            onClick={() =>
                                                setHtmlPreview({
                                                    to: item.to,
                                                    body: decodeHtmlEntities(item.message),
                                                    templateId: item.message_template_id,
                                                })
                                            }
                                        >
                                            This message contains HTML. Click to preview.
                                        </Text>
                                    ) : (item.message || "—")}
                                </Td>

                                <Td>
                                    {(() => {
                                        const channel = Channels.find((c) => c.id === item?.MsgChannel?.id) ||
                                            Channels.find((c) => c.name === item?.MsgChannel?.name);
                                        const IconComp = channel?.icon || FaMobileAlt;
                                        return (
                                            <>
                                                <HStack spacing={2}>
                                                    <Icon as={IconComp} color={channel?.color || "gray"} boxSize={4} />
                                                    <Text fontSize="sm" fontWeight="thin">
                                                        {channel?.name === "In-App Notification" ? "In-App" : channel?.name || "N/A"}
                                                    </Text>
                                                </HStack>
                                                {/* SMS parts can legitimately be missing (e.g. before delivery
                                                    is confirmed) — show nothing rather than "Count: undefined". */}
                                                {channel?.name === "SMS" && item?.message_count != null && (
                                                    <Text fontSize="xs" color="gray.500">Count: {item.message_count}</Text>
                                                )}
                                            </>
                                        );
                                    })()}
                                </Td>

                                <Td whiteSpace="nowrap" verticalAlign="top">
                                    <VStack align="flex-start" spacing={0}>
                                        <Text color="#64748b" fontSize="xs">
                                            Received: {moment.utc(item.receive_date).tz(getUserTimeZone()).format("DD MMM YY hh:mm:ss A")}
                                        </Text>
                                        <Text color="#64748b" fontSize="xs">
                                            Sent: {moment.utc(item.sent_date).tz(getUserTimeZone()).format("DD MMM YY hh:mm:ss A")}
                                        </Text>
                                    </VStack>
                                </Td>

                                <Td>
                                    <HStack spacing={2}>
                                        <HStack
                                            spacing={1.5}
                                            display="inline-flex"
                                            alignItems="center"
                                            px={2.5}
                                            py={1}
                                            borderRadius="full"
                                            border="1px solid"
                                            borderColor={`${item?.Status?.color || "gray"}.200`}
                                            bg={`${item?.Status?.color || "gray"}.50`}
                                        >
                                            <Box
                                                w="6px"
                                                h="6px"
                                                borderRadius="full"
                                                bg={`${item?.Status?.color || "gray"}.500`}
                                                flexShrink={0}
                                            />
                                            <Text
                                                fontSize="xs"
                                                color={`${item?.Status?.color || "gray"}.700`}
                                                fontWeight="500"
                                                whiteSpace="nowrap"
                                            >
                                                {item?.Status?.name || "Unknown"}
                                            </Text>
                                        </HStack>
                                        {item.error_log && (
                                            <Tooltip label="View error details" hasArrow>
                                                {/* Plain info glyph — the status pill beside it
                                                    already carries the failure colour. */}
                                                <Box
                                                    as="span"
                                                    display="inline-flex" alignItems="center"
                                                    cursor="pointer" color="red.500"
                                                    _hover={{ color: "red.600" }}
                                                    onClick={() => {
                                                        setSelectedErrorLog(item.error_log);
                                                        onErrorLogOpen();
                                                    }}
                                                >
                                                    <Icon as={LuInfo} boxSize={4} />
                                                </Box>
                                            </Tooltip>
                                        )}
                                    </HStack>
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>

                {totalItems > 0 && (
                    <Flex justify="center" mt={6}>
                        <Pagination
                            activePage={activePage}
                            itemsCountPerPage={100}
                            totalItemsCount={totalItems}
                            pageRangeDisplayed={10}
                            onChange={(page) => onPageChange?.(page)}
                            itemClass="page-item"
                            linkClass="page-link"
                            innerClass="pagination"
                        />
                    </Flex>
                )}
            </Box>

            {/* Message detail modal */}
            <Modal isOpen={isOpen} onClose={closeModal} size="xl" isCentered>
                <ModalOverlay />
                <ModalContent maxW="600px">
                    <ModalHeader borderBottom="1px" borderColor="gray.200" pb={3} fontSize="lg" fontWeight="semibold">
                        <HStack spacing={2}>
                            <Icon as={TimeIcon} color="blue.500" />
                            <Text>Message Delivery Journey</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody py={4}>
                        {selectedMessage ? (
                            <VStack align="stretch" spacing={4}>
                                <Box bg="gray.50" borderRadius="md">
                                    <Box bg="red.50" boxShadow="sm" borderRadius="lg" border={1} borderColor="red.200" borderStyle="solid" mb={2}>
                                        <Text fontSize="xs" color="black" p={2}>
                                            <Flex justifyContent="space-between" alignItems="center">
                                                <strong>Message Id: {selectedMessage.messageid || selectedMessage._id}</strong>
                                                <Button size="xs" bg="rgb(var(--brand-primary-rgb) / 0.902)" color="white"
                                                    onClick={() => handleViewPayload(selectedMessage.customer_payload)}>
                                                    View Payload
                                                </Button>
                                            </Flex>
                                        </Text>
                                    </Box>
                                    <Text fontSize="sm" color="gray.600">
                                        Complete delivery timeline showing channel-specific status progression and auto-failover attempts.
                                    </Text>
                                </Box>

                                <Box bg="blue.50" border="1px" borderColor="blue.200" borderRadius="md" p={3}>
                                    <Flex justify="space-between" mb={2}>
                                        <Box flex="1">
                                            <Text fontSize="xs" color="gray.600" mb={1}>Recipient</Text>
                                            <Text fontSize="xs" fontWeight="semibold">{selectedMessage.to || "-"}</Text>
                                        </Box>
                                        <Box flex="1">
                                            <Text fontSize="xs" color="gray.600" mb={1}>Message Type</Text>
                                            <Tag fontSize="xs" fontWeight="normal" border="1px" borderColor="gray.300" color="black" bg="white">
                                                {getTypeId(selectedMessage.message_type_id)}
                                            </Tag>
                                        </Box>
                                        <Box flex="1">
                                            <Text fontSize="xs" color="gray.600" mb={1}>Final Status</Text>
                                            <Badge colorScheme={getStatusColor(selectedMessage?.Status?.name)} px={2} py={1} borderRadius="md">
                                                {selectedMessage?.Status?.name || "Unknown"}
                                            </Badge>
                                        </Box>
                                    </Flex>

                                    {selectedMessage.message && (
                                        <>
                                            <Text fontSize="xs" color="gray.600" mb={2} fontWeight="medium">Message Content</Text>
                                            <Box bg="white" border={1} borderColor="gray.100" p={3} borderRadius="md">
                                                {regexCheckHtml.test(selectedMessage.message) ? (
                                                    // The log pipeline masks every digit in the stored body to ****,
                                                    // which destroys HTML beyond repair (borders, padding, image
                                                    // URLs). Route to EmailPreviewModal, which offers the source
                                                    // template — the only accurate render of this message.
                                                    <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
                                                        <Text fontSize="sm" color="gray.600">
                                                            HTML email content · {selectedMessage.message.length.toLocaleString()} characters
                                                        </Text>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            flexShrink={0}
                                                            onClick={() =>
                                                                setHtmlPreview({
                                                                    to: selectedMessage.to,
                                                                    body: decodeHtmlEntities(selectedMessage.message),
                                                                    templateId: selectedMessage.message_template_id,
                                                                })
                                                            }
                                                        >
                                                            Preview HTML
                                                        </Button>
                                                    </Flex>
                                                ) : <Text fontSize="sm">{selectedMessage.message}</Text>}
                                            </Box>
                                        </>
                                    )}
                                </Box>

                                <Box>
                                    <HStack spacing={2} mb={3}>
                                        <Icon as={TimeIcon} color="blue.500" boxSize={4} />
                                        <Text fontSize="sm" fontWeight="semibold">Delivery Timeline & Status Progression</Text>
                                    </HStack>
                                    <VStack align="stretch" spacing={0} pl={2}>
                                        {selectedMessage.history?.map((h, i) => {
                                            const status = getStatusDetails(h.status_id);
                                            const statusFallback = KNOWN_STATUS_FALLBACKS[h.status_id];
                                            const displayStatusName =
                                                status?.name || statusFallback?.name || `Status ${h.status_id}`;
                                            const isSuccessStatus =
                                                (status?.msg_status ?? statusFallback?.msg_status) === "1";
                                            const { name: channelName, icon: ChannelIcon, color: channelColor } = getChannelInfo(h.channel_id);
                                            const isFailed = status?.name?.toLowerCase().includes("fail") || status?.name?.toLowerCase().includes("undeliverable");
                                            const nextStatus = selectedMessage.history[i + 1];
                                            const nextStatusDetails = nextStatus ? getStatusDetails(nextStatus.status_id) : null;
                                            const isNextNotFailed = nextStatusDetails && !nextStatusDetails?.name?.toLowerCase().includes("fail") && !nextStatusDetails?.name?.toLowerCase().includes("undeliverable");
                                            const shouldTriggerFailover = isFailed && isNextNotFailed;

                                            const getBg = (s) => {
                                                if (!s) return "white";
                                                const n = s.name?.toLowerCase() || "";
                                                if (n.includes("read") || n.includes("delivered") || n.includes("clicked")) return "#f0fdf4";
                                                if (n.includes("sent")) return "var(--brand-tint)";
                                                if (n.includes("fail") || n.includes("undeliverable") || n.includes("invalid")) return "#fef2f2";
                                                return "white";
                                            };

                                            const getBorder = (s) => {
                                                if (!s) return "gray";
                                                const n = s.name?.toLowerCase() || "";
                                                if (n.includes("read") || n.includes("delivered") || n.includes("clicked")) return "#00c951";
                                                if (n.includes("sent")) return "#2b7fff";
                                                if (n.includes("fail") || n.includes("undeliverable") || n.includes("invalid")) return "#fb2c36";
                                                if (n.includes("accept")) return "#6a7282";
                                                return "gray";
                                            };

                                            return (
                                                <Flex key={i}>
                                                    <Box flex="1" bg={getBg(status)} border="1px" borderColor={getBg(status)}
                                                        borderLeftWidth="4px" borderLeftColor={getBorder(status)}
                                                        borderRadius="md" my={3} px={2} py={5}>
                                                        <Flex align="start" gap={3}>
                                                            {isSuccessStatus
                                                                ? <FiCheckCircle color="#00a63e" />
                                                                : <FiXCircle color="#e7000b" />}
                                                            <Box flex="1">
                                                                <Flex justify="space-between" align="start" mb={1}>
                                                                    <HStack spacing={2}>
                                                                        <HStack spacing={1}>
                                                                            <Icon as={ChannelIcon} color={channelColor} boxSize={3.5} />
                                                                            <Text fontSize="xs" fontWeight="semibold">{channelName}</Text>
                                                                        </HStack>
                                                                        {status?.msg_status === "0" && <Tag bg="red" size="sm" color="white">Failed</Tag>}
                                                                        <Tag size="sm" border="1px solid" borderColor="gray.300"
                                                                            color={status?.name?.toLowerCase().includes("accept") ? "black" : "#64748b"}
                                                                            bg="transparent">
                                                                            {displayStatusName}
                                                                        </Tag>
                                                                    </HStack>
                                                                    <Text fontSize="xs" color="gray.500">
                                                                        {new Date(h.time).toLocaleString("en-PK", { dateStyle: "short", timeStyle: "medium" })}
                                                                    </Text>
                                                                </Flex>
                                                                {(h.channel_id === 5 || h.channel_id === 6) && (
                                                                    <Text fontSize="xs" color="gray.600">Subscriber Id: {h.subscriber_id || '-'}</Text>
                                                                )}
                                                                {h.status_id === 110 && (
                                                                    <Text fontSize="xs" color="gray.600">Message Sent to device</Text>
                                                                )}

                                                                {isFailed ? (
                                                                    <Button
                                                                        size="xs"
                                                                        variant="outline"
                                                                        colorScheme="red"
                                                                        leftIcon={<WarningIcon />}
                                                                        mt={1}
                                                                        onClick={() => {
                                                                            // History entries rarely carry their own error_log — the
                                                                            // real payload almost always lives on the top-level message.
                                                                            setSelectedErrorLog(h.error_log || selectedMessage?.error_log);
                                                                            onErrorLogOpen();
                                                                        }}
                                                                    >
                                                                        Message Undeliverable — View Error Details
                                                                    </Button>
                                                                ) : (
                                                                    <Text fontSize="xs" color="gray.600">
                                                                        {status?.description ||
                                                                            (status?.name === "Message Accepted"
                                                                                ? "Message Accepted - Request received and validated"
                                                                                : status?.name === "Message Sent"
                                                                                    ? `Message Sent to ${channelName}`
                                                                                    : status?.name === "Message Delivered"
                                                                                        ? "Message Delivered"
                                                                                        : status?.name === "Message Read"
                                                                                            ? "Message Read"
                                                                                            : "")}
                                                                    </Text>
                                                                )}
                                                                {shouldTriggerFailover && (
                                                                    <Text fontSize="xs" color="#ca3500" bg="#ffedd4" px={2} py={1} mt={2} borderRadius="md" fontWeight="medium">
                                                                        → Triggering auto-failover to next channel
                                                                    </Text>
                                                                )}
                                                            </Box>
                                                        </Flex>
                                                    </Box>
                                                </Flex>
                                            );
                                        })}
                                    </VStack>
                                </Box>
                            </VStack>
                        ) : <Text>No details available.</Text>}
                    </ModalBody>
                    <ModalFooter borderTop="1px" borderColor="gray.200">
                        <Button onClick={closeModal} size="sm" bg="var(--brand-primary)" color="white">Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Error Log Modal */}
            <Modal isOpen={isErrorLogOpen} onClose={onErrorLogClose} size="xl" isCentered>
                <ModalOverlay />
                <ModalContent maxW="700px">
                    <ModalHeader borderBottom="1px" borderColor="gray.100" pb={3}>
                        <HStack spacing={2}>
                            <Box display="inline-flex" alignItems="center" justifyContent="center"
                                w="28px" h="28px" borderRadius="full" bg="blue.50" border="1px solid" borderColor="blue.200">
                                <WarningIcon color="blue.500" boxSize={3} />
                            </Box>
                            <Text fontSize="md" fontWeight="600" color="gray.800">Error Details</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton top={3} />
                    <ModalBody py={4}>
                        {(() => {
                            const raw = selectedErrorLog;
                            if (!raw) return <Text color="gray.400" fontSize="sm">No error details available.</Text>;

                            let parsed = raw;
                            if (typeof raw === 'string') {
                                try { parsed = JSON.parse(raw); } catch { parsed = raw; }
                            }

                            const flattenObject = (obj, prefix = '') => {
                                if (typeof obj !== 'object' || obj === null) return { [prefix]: obj };
                                return Object.entries(obj).reduce((acc, [key, val]) => {
                                    const newKey = prefix ? `${prefix}.${key}` : key;
                                    if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
                                        Object.assign(acc, flattenObject(val, newKey));
                                    } else { acc[newKey] = val; }
                                    return acc;
                                }, {});
                            };

                            const renderValue = (val) => {
                                if (typeof val === 'boolean') return (
                                    <Tag size="sm" borderRadius="full"
                                        bg={val ? '#dcfce7' : '#fee2e2'} color={val ? '#16a34a' : '#dc2626'}>
                                        {String(val)}
                                    </Tag>
                                );
                                if (val === null || val === undefined) return <Text fontSize="sm" color="gray.400">—</Text>;
                                if (Array.isArray(val)) return <Text fontSize="sm" color="gray.700">{JSON.stringify(val)}</Text>;
                                return <Text fontSize="sm" color="gray.700">{String(val)}</Text>;
                            };

                            const flatData = Array.isArray(parsed)
                                // A single-item array (the overwhelmingly common case) doesn't need an
                                // "[0]." prefix on every key — it only exists to disambiguate multiple
                                // items, and otherwise just makes keys longer and harder to read.
                                ? (parsed.length === 1
                                    ? flattenObject(parsed[0])
                                    : parsed.reduce((acc, item, i) => Object.assign(acc, flattenObject(item, `[${i}]`)), {}))
                                : typeof parsed === 'object'
                                    ? flattenObject(parsed)
                                    : { value: parsed };

                            // "value" as a raw key (e.g. a bounce webhook's `{ value: "Bounce" }`) reads
                            // better to a customer as "Reason" — display-only relabel, key untouched.
                            const displayKey = (key) => (key.toLowerCase() === 'value' ? 'Reason' : key);

                            return (
                                <TableContainer border="1px" borderColor="gray.200" borderRadius="md">
                                    <Table variant="simple" size="sm" tableLayout="fixed">
                                        <Thead bg="gray.50">
                                            <Tr>
                                                <Th py={3} fontSize="xs" color="gray.700" fontWeight="700" w="180px">Key</Th>
                                                <Th py={3} fontSize="xs" color="gray.700" fontWeight="700">Value</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {Object.entries(flatData).map(([key, val]) => (
                                                <Tr key={key} _hover={{ bg: 'gray.50' }}>
                                                    <Td py={3} fontSize="sm" color="gray.600" borderColor="gray.100"
                                                        whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" title={key}>{displayKey(key)}</Td>
                                                    <Td py={3} borderColor="gray.100" wordBreak="break-word" whiteSpace="normal">{renderValue(val)}</Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            );
                        })()}
                    </ModalBody>
                    <ModalFooter borderTop="1px" borderColor="gray.100">
                        <Button onClick={onErrorLogClose} size="sm" bg="var(--brand-primary)" color="white" _hover={{ bg: 'var(--brand-primary-hover)' }}>
                            Close
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Payload modal */}
            <Modal isOpen={isPayloadOpen} onClose={onPayloadClose} size="4xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Payload Details</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {selectedPayload ? (
                            <Box maxH="400px" overflowY="auto" color="green.200" borderRadius="md">
                                <pre style={{ backgroundColor: "black", textShadow: "none", color: "white", padding: "0px", borderRadius: "8px", fontFamily: "monospace" }}>
                                    <span style={{ padding: 15, backgroundColor: "#2F2F2F", fontSize: "14px", display: "block", marginBottom: "10px" }}>json</span>
                                    <code className="language-json" style={{ padding: "0px 15px 15px", display: "block", whiteSpace: "pre-wrap" }}
                                        dangerouslySetInnerHTML={{ __html: Prism.highlight(selectedPayload, Prism.languages.json, 'json') }} />
                                </pre>
                            </Box>
                        ) : <Text>No payload found.</Text>}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onPayloadClose} size="sm" bg="var(--brand-primary)" color="white">Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* HTML message body, opened from the Message column or the journey modal.
                Rendered in the shared sandboxed-iframe modal so the email displays at
                full size and its CSS cannot leak into the app. */}
            <EmailPreviewModal
                isEmailPreviewOpen={!!htmlPreview}
                onEmailPreviewClose={() => setHtmlPreview(null)}
                title={htmlPreview?.fromTemplate ? "Message Preview — source template" : "Message Preview"}
                subjectLabel="Recipient:"
                subject={htmlPreview?.to || ''}
                body={htmlPreview?.body}
                onFallbackPreview={
                    htmlPreview?.templateId && !htmlPreview?.fromTemplate
                        ? () => loadTemplatePreview(htmlPreview.templateId)
                        : undefined
                }
                fallbackLabel={`Preview template #${htmlPreview?.templateId} instead`}
                fallbackLoading={templatePreviewLoading}
                fallbackNote={
                    htmlPreview?.fromTemplate
                        ? 'Showing the source template, not the delivered message — variables are unsubstituted and later template edits will show through.'
                        : undefined
                }
            />
        </>
    );
}